// src/utils/policyTemplates.js
// Template generation functions for Privacy Policy and Terms of Service

/**
 * Generate Privacy Policy HTML from questionnaire answers
 * @param {Object} answers - Questionnaire answers
 * @param {Object} practitionerData - Practitioner info (name, email, location)
 * @returns {string} HTML formatted privacy policy
 */
export const generatePrivacyPolicy = (answers, practitionerData = {}) => {
  const sections = [];
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const businessName = practitionerData.displayName || 'us';
  const contactEmail = practitionerData.email || '';

  // Title
  sections.push(`<h2>Privacy Policy</h2>`);
  sections.push(`<p><em>Last updated: ${today}</em></p>`);
  sections.push(`<p>Your privacy is important to ${businessName}. This Privacy Policy explains how we collect, use, and protect your personal information when you use our services or visit our website.</p>`);

  // Section 1: Information We Collect
  sections.push(`<h3>Information We Collect</h3>`);

  let collectItems = [];

  if (answers.collectNamesEmails) {
    collectItems.push('Name and email address (when you book a session, submit a contact form, or subscribe to our newsletter)');
  }

  collectItems.push('Phone number (if provided for appointment reminders)');

  if (answers.collectIntakeForms) {
    collectItems.push('Information you provide on intake forms (goals, concerns, preferences)');
  }

  if (answers.collectHealthHistory) {
    collectItems.push('Health history or personal circumstances you share to help us serve you better');
  }

  if (answers.thirdPartyServices?.includes('stripe')) {
    collectItems.push('Payment information is processed securely through Stripe — we never store your credit card details on our servers');
  }

  // Build the list as HTML
  sections.push(`<p>We may collect the following information:</p>`);
  sections.push(`<ul>${collectItems.map(item => `<li>${item}</li>`).join('')}</ul>`);

  // Section 2: How We Use Your Information
  sections.push(`<h3>How We Use Your Information</h3>`);
  sections.push(`<p>We use the information we collect to:</p>`);
  let useItems = [
    'Schedule and manage your appointments',
    'Communicate with you about your sessions',
    'Provide the services you have requested',
    'Send appointment reminders and follow-up communications',
  ];

  if (answers.collectIntakeForms || answers.collectHealthHistory) {
    useItems.push('Prepare for your sessions and provide personalized guidance');
  }

  sections.push(`<ul>${useItems.map(item => `<li>${item}</li>`).join('')}</ul>`);
  sections.push(`<p>We will never sell, rent, or share your personal information with third parties for marketing purposes.</p>`);

  // Section 3: Third-Party Services
  if (answers.thirdPartyServices?.length > 0) {
    sections.push(`<h3>Third-Party Services</h3>`);
    sections.push(`<p>We use trusted third-party services to operate our practice:</p>`);

    let tpItems = [];
    if (answers.thirdPartyServices.includes('stripe')) {
      tpItems.push('<strong>Stripe</strong> for secure payment processing. Stripe\'s privacy policy can be found at stripe.com/privacy.');
    }
    if (answers.thirdPartyServices.includes('zoom')) {
      tpItems.push('<strong>Zoom</strong> for virtual sessions. Zoom\'s privacy policy can be found at zoom.us/privacy.');
    }
    if (answers.thirdPartyServices.includes('google_calendar')) {
      tpItems.push('<strong>Google Calendar</strong> for scheduling. Google\'s privacy policy can be found at policies.google.com/privacy.');
    }
    if (answers.thirdPartyServices.includes('mailchimp')) {
      tpItems.push('<strong>Email service provider</strong> for newsletters and updates. You can unsubscribe at any time using the link in any email.');
    }

    sections.push(`<ul>${tpItems.map(item => `<li>${item}</li>`).join('')}</ul>`);
    sections.push(`<p>These services have their own privacy policies governing how they handle your data.</p>`);
  }

  // Section 4: Data Security
  sections.push(`<h3>Data Security</h3>`);
  sections.push(`<p>We take reasonable measures to protect your personal information from unauthorized access, disclosure, or misuse. Your data is stored securely and access is limited to what is necessary to provide our services.</p>`);

  if (answers.collectHealthHistory) {
    sections.push(`<p>Sensitive information such as health history is treated with additional care and confidentiality, consistent with the trust you place in our practitioner-client relationship.</p>`);
  }

  // Section 5: Cookies & Analytics
  if (answers.useAnalytics) {
    sections.push(`<h3>Cookies and Analytics</h3>`);
    sections.push(`<p>Our website may use cookies and analytics tools to understand how visitors use our site. This helps us improve your experience. You can disable cookies in your browser settings at any time, though some features may not function as expected.</p>`);
  }

  // Section 6: Your Rights
  sections.push(`<h3>Your Rights</h3>`);
  sections.push(`<p>You have the right to:</p>`);
  let rightsItems = [
    'Request access to the personal information we hold about you',
    'Request correction of any inaccurate information',
    'Request deletion of your personal information',
    'Withdraw consent for communications at any time',
  ];

  // California-specific (CCPA)
  const location = (answers.businessLocation || '').toLowerCase();
  if (location.includes('california') || location.includes('ca')) {
    sections.push(`<ul>${rightsItems.map(item => `<li>${item}</li>`).join('')}</ul>`);
    sections.push(`<h3>California Privacy Rights</h3>`);
    sections.push(`<p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to request deletion, and the right to opt out of the sale of personal information. We do not sell personal information.</p>`);
  } else {
    sections.push(`<ul>${rightsItems.map(item => `<li>${item}</li>`).join('')}</ul>`);
  }

  // Section 7: Children's Privacy
  if (answers.workWithMinors) {
    sections.push(`<h3>Children's Privacy</h3>`);
    sections.push(`<p>We may provide services to individuals under 18 with appropriate parental or guardian consent. We take additional steps to protect the privacy of minors and only collect information necessary to provide our services. A parent or guardian may request access to, correction, or deletion of their child's information at any time.</p>`);
  }

  // Section 8: Changes to This Policy
  sections.push(`<h3>Changes to This Policy</h3>`);
  sections.push(`<p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.</p>`);

  // Section 9: Contact
  sections.push(`<h3>Contact Us</h3>`);
  if (contactEmail) {
    sections.push(`<p>If you have questions about this Privacy Policy or how we handle your information, please contact us at ${contactEmail}.</p>`);
  } else {
    sections.push(`<p>If you have questions about this Privacy Policy or how we handle your information, please reach out using the contact information on our website.</p>`);
  }

  return sections.join('\n');
};

/**
 * Generate Terms of Service HTML from questionnaire answers
 * @param {Object} answers - Questionnaire answers
 * @param {Object} practitionerData - Practitioner info (name, email, cancellation policy)
 * @returns {string} HTML formatted terms of service
 */
export const generateTermsOfService = (answers, practitionerData = {}) => {
  const sections = [];
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const businessName = practitionerData.displayName || 'us';
  const contactEmail = practitionerData.email || '';
  const cancellationPolicy = practitionerData.cancellationPolicy;

  // Title
  sections.push(`<h2>Terms of Service</h2>`);
  sections.push(`<p><em>Last updated: ${today}</em></p>`);
  sections.push(`<p>Welcome to ${businessName}. By booking a session or using our services, you agree to the following terms.</p>`);

  // Section 1: Scope of Services
  sections.push(`<h3>Services Offered</h3>`);
  const serviceTypes = answers.serviceTypes || 'the services listed on our website';
  sections.push(`<p>We offer the following services: ${serviceTypes}.</p>`);
  sections.push(`<p>All services are provided for personal growth, wellness, and spiritual guidance purposes.</p>`);

  // Section 2: Booking and Scheduling
  sections.push(`<h3>Booking and Scheduling</h3>`);
  sections.push(`<p>Sessions are booked through our website and are confirmed upon receipt of payment. By booking, you agree to arrive on time and prepared for your session.</p>`);
  sections.push(`<p>We reserve the right to reschedule or cancel sessions due to unforeseen circumstances, in which case a full refund or rescheduled session will be offered.</p>`);

  // Section 3: Cancellation and Refund Policy
  sections.push(`<h3>Cancellation and Refund Policy</h3>`);
  if (cancellationPolicy) {
    const timeframeMap = {
      '24h': '24 hours', '48h': '48 hours', '72h': '72 hours',
      '1week': 'one week', 'custom': cancellationPolicy.customTimeframe || 'the specified timeframe'
    };
    const refundMap = {
      'full': 'a full refund', '50': 'a 50% refund',
      'none': 'no refund', 'custom': cancellationPolicy.customRefund || 'a refund as specified'
    };
    const timeframe = timeframeMap[cancellationPolicy.timeframe] || '24 hours';
    const refund = refundMap[cancellationPolicy.refundPercentage] || 'a full refund';

    sections.push(`<p>Cancellations made at least ${timeframe} before your scheduled session are eligible for ${refund}. Cancellations made after this window may not be eligible for a refund.</p>`);

    if (cancellationPolicy.noShowPolicy) {
      sections.push(`<p>${cancellationPolicy.noShowPolicy}</p>`);
    }
  } else {
    sections.push(`<p>Please provide at least 24 hours' notice if you need to cancel or reschedule your session. Late cancellations and no-shows may be subject to a fee.</p>`);
  }

  // Section 4: Health and Wellness Disclaimer (conditional)
  if (answers.healthMedicalDisclaimer) {
    sections.push(`<h3>Health and Wellness Disclaimer</h3>`);
    sections.push(`<p>The services provided are intended for spiritual guidance and personal growth. They are not a substitute for professional medical, psychological, or psychiatric advice, diagnosis, or treatment. If you are experiencing a medical or mental health emergency, please contact your healthcare provider or call 911.</p>`);
    sections.push(`<p>By booking a session, you acknowledge that our services do not replace professional medical care and that any decisions you make based on the guidance provided are your own responsibility.</p>`);
  }

  // Section 5: Financial and Legal Disclaimer (conditional)
  if (answers.financialLegalDisclaimer) {
    sections.push(`<h3>Financial and Legal Disclaimer</h3>`);
    sections.push(`<p>Our services are not a substitute for professional financial, legal, or tax advice. Any guidance provided during sessions is for informational and personal growth purposes only. We recommend consulting a qualified professional for specific financial or legal matters.</p>`);
  }

  // Section 6: Client Responsibilities
  sections.push(`<h3>Client Responsibilities</h3>`);
  sections.push(`<p>By booking a session, you agree to:</p>`);
  sections.push(`<ul><li>Provide accurate information when booking and during intake</li><li>Arrive on time for your scheduled session</li><li>Treat the practitioner with respect and courtesy</li><li>Communicate openly about your needs and boundaries</li></ul>`);

  // Section 7: Age Requirements (conditional)
  if (answers.minimumAge === '18+') {
    sections.push(`<h3>Age Requirements</h3>`);
    sections.push(`<p>Our services are available to individuals 18 years of age and older. By booking a session, you confirm that you are at least 18 years old.</p>`);
  } else if (answers.minimumAge === '16+') {
    sections.push(`<h3>Age Requirements</h3>`);
    sections.push(`<p>Clients under 18 may book sessions with written consent from a parent or legal guardian. The parent or guardian is responsible for reviewing these terms and providing authorization.</p>`);
  }
  // If 'none', skip this section

  // Section 8: Pre-Paid Services (conditional)
  if (answers.offersPackages) {
    sections.push(`<h3>Gift Cards and Pre-Paid Packages</h3>`);
    sections.push(`<p>Gift cards and pre-paid session packages are non-transferable unless otherwise stated. Unused sessions do not expire unless a specific expiration date is noted at the time of purchase. Refunds for unused portions of packages may be available at our discretion.</p>`);
  }

  // Section 9: Intellectual Property
  sections.push(`<h3>Intellectual Property</h3>`);
  sections.push(`<p>All content, materials, and resources provided during sessions remain the intellectual property of ${businessName}. Sessions may not be recorded, reproduced, or distributed without prior written consent.</p>`);

  // Section 10: Limitation of Liability
  sections.push(`<h3>Limitation of Liability</h3>`);
  sections.push(`<p>${businessName} is not liable for any outcomes, decisions, or actions taken based on the guidance or information provided during sessions. Our services are offered in good faith and to the best of our professional ability.</p>`);

  // Section 11: Dispute Resolution
  sections.push(`<h3>Dispute Resolution</h3>`);
  if (answers.disputeResolution === 'mediation') {
    sections.push(`<p>In the event of a dispute, both parties agree to attempt resolution through good-faith mediation before pursuing any other legal action.</p>`);
  } else if (answers.disputeResolution === 'arbitration') {
    sections.push(`<p>Any disputes arising from these terms or services provided shall be resolved through binding arbitration in accordance with applicable rules and regulations.</p>`);
  } else if (answers.disputeResolution === 'small_claims') {
    sections.push(`<p>Any disputes arising from these terms or services provided may be brought before the appropriate small claims court in the jurisdiction where the practitioner is located.</p>`);
  }

  // Section 12: Changes to Terms
  sections.push(`<h3>Changes to These Terms</h3>`);
  sections.push(`<p>We may update these Terms of Service from time to time. Any changes will be posted on this page with an updated revision date. Continued use of our services after changes are posted constitutes acceptance of the updated terms.</p>`);

  // Section 13: Contact
  sections.push(`<h3>Contact Us</h3>`);
  if (contactEmail) {
    sections.push(`<p>If you have questions about these Terms of Service, please contact us at ${contactEmail}.</p>`);
  } else {
    sections.push(`<p>If you have questions about these Terms of Service, please reach out using the contact information on our website.</p>`);
  }

  return sections.join('\n');
};
